import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { defineMessages } from "react-intl";
import classNames from "classnames";

import Button from "../../../Buttons/Button";
import {
  CONTRIBUTIONS_SORT_ORDERS,
  CONTRIBUTIONS_SORT_ORDER_RANDOM
} from "./ContributionsDatavizSorting";
import { useResourcesList } from "../../../ResourcesList/resourcesList";
import { useFormatMessage } from "../../../Intl/Context";
import {
  regenerateSortOrderSeed,
  updateSortOrder
} from "../../../../reducers/resourcesList/sortOrder";

const ContributionsDatavizSortingButton = ({ sortOrderKey }) => {
  const {
    resourcesListKey,
    sortOrder: { field: currentSortField }
  } = useResourcesList();
  const sortOrderConfig = CONTRIBUTIONS_SORT_ORDERS[sortOrderKey];
  const { field: sortField, direction: sortDirection, requiresSeed } = sortOrderConfig;
  const isActive = currentSortField === sortField;
  const isRandom = sortOrderConfig === CONTRIBUTIONS_SORT_ORDER_RANDOM;

  const t = useFormatMessage();
  const label = t(intlMessages[sortOrderKey]);
  const screenReaderLabelKey = [sortOrderKey, "ScreenReaderLabel"].join("");
  const screenReaderLabel = t(intlMessages[screenReaderLabelKey]);

  const dispatch = useDispatch();

  const handleClick = useCallback(() => {
    // Do not update the sort order if it's already the active one
    if (isActive && !isRandom) return;
    // If we click again on the "Random" sort button, we regenerate the seed so the order
    // is randomized again.
    if (isActive && isRandom) {
      return dispatch(regenerateSortOrderSeed(resourcesListKey));
    }

    dispatch(updateSortOrder(resourcesListKey, sortField, sortDirection, requiresSeed));
  }, [
    isActive,
    isRandom,
    dispatch,
    resourcesListKey,
    sortField,
    sortDirection,
    requiresSeed
  ]);

  const className = classNames("btn", "nowrap", "box-shadow", {
    "btn-primary-inverse": !isActive,
    "btn-primary": isActive,
    "btn-active": isActive
  });

  return (
    <Button
      onClick={handleClick}
      type="button"
      className={className}
      label={label}
      labelClassName="text-center"
      screenReaderLabel={screenReaderLabel}
    />
  );
};

export default ContributionsDatavizSortingButton;

const intlMessages = defineMessages({
  random: {
    id: "contributions.dataviz.sorting.random",
    defaultMessage: "Aléatoire"
  },
  randomScreenReaderLabel: {
    id: "contributions.dataviz.sorting.randomScreenReaderLabel",
    defaultMessage: "Trier les contributions de manière aléatoire"
  },
  mostAnimated: {
    id: "contributions.dataviz.sorting.mostAnimated",
    defaultMessage: "+ Animées"
  },
  mostAnimatedScreenReaderLabel: {
    id: "contributions.dataviz.sorting.mostAnimatedScreenReaderLabel",
    defaultMessage: "Trier les contributions des plus animées aux moins animées"
  },
  mostRecent: {
    id: "contributions.dataviz.sorting.mostRecent",
    defaultMessage: "+ Récentes"
  },
  mostRecentScreenReaderLabel: {
    id: "contributions.dataviz.sorting.mostRecentScreenReaderLabel",
    defaultMessage: "Trier les contributions des plus récentes aux plus anciennes"
  }
});
